import { useState } from 'react';
import type { ResultadoCupon } from '@/utils/pedido';
import type { CuponConfig } from '@/services/store.service';
import { formatCurrency } from '@/utils/helpers';

interface Props {
  config: CuponConfig | null;
  codigo: string | null;
  resultado: ResultadoCupon | null;
  onAplicar: (codigo: string) => string | null;
  onQuitar: () => void;
}

/** Campo del cupón de vendedor en el carrito, con su estado. */
export default function CuponCarrito({ config, codigo, resultado, onAplicar, onQuitar }: Props) {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [error, setError] = useState('');

  if (!config) return null;
  const condicion = `Se aplica al comprar un kit solar o más de ${formatCurrency(config.minimoUsd)}`;

  if (codigo && resultado) {
    return (
      <div
        className="rounded-xl px-3.5 py-2.5 border"
        style={resultado.aplica
          ? { background: 'rgba(34,197,94,0.08)', borderColor: 'rgba(34,197,94,0.3)' }
          : { background: '#fffbeb', borderColor: '#fde68a' }}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-[13px] font-bold text-stone-800">
            <i className="ri-coupon-3-line text-base" style={{ color: resultado.aplica ? '#16a34a' : '#d97706' }} />
            Cupón {resultado.codigo}
          </span>
          <span className="flex items-center gap-2">
            {resultado.aplica && (
              <span className="text-[13px] font-black text-emerald-600">−{formatCurrency(resultado.descuento)}</span>
            )}
            <button onClick={onQuitar} aria-label="Quitar cupón" className="text-stone-400 hover:text-stone-700 cursor-pointer">
              <i className="ri-close-line text-base" />
            </button>
          </span>
        </div>
        <p className="text-[11px] mt-0.5" style={{ color: resultado.aplica ? '#15803d' : '#b45309' }}>
          {resultado.aplica ? `Cupón de ${resultado.vendedor.nombre} aplicado a toda la compra` : condicion}
        </p>
      </div>
    );
  }

  if (!abierto) {
    return (
      <button
        onClick={() => setAbierto(true)}
        className="flex items-center gap-1.5 text-[12px] font-semibold text-[#1e8aab] hover:text-[#1e2d4a] cursor-pointer px-1"
      >
        <i className="ri-coupon-3-line" /> ¿Tienes un cupón de descuento?
      </button>
    );
  }

  const aplicar = () => {
    const e = onAplicar(texto);
    setError(e ?? '');
    if (!e) { setTexto(''); setAbierto(false); }
  };

  return (
    <div>
      <div className="flex gap-2">
        <input
          value={texto}
          onChange={(e) => { setTexto(e.target.value.toUpperCase()); setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') aplicar(); }}
          placeholder="CÓDIGO"
          aria-label="Código del cupón"
          autoFocus
          className="flex-1 min-w-0 h-10 px-3 rounded-xl border border-stone-200 font-mono text-sm uppercase focus:outline-none focus:border-[#4bbfdf]"
          style={{ fontSize: 16 }}
        />
        <button onClick={aplicar} className="h-10 px-4 rounded-xl bg-[#1e2d4a] text-white text-sm font-bold cursor-pointer">
          Aplicar
        </button>
      </div>
      <p className="text-[11px] mt-1 px-1" style={{ color: error ? '#dc2626' : '#78716c' }}>{error || condicion}</p>
    </div>
  );
}
